import { Injectable } from "@angular/core";
import Keycloak from "keycloak-js";

@Injectable({ providedIn: "root" })
export class OnboardingSessionService {
  private static readonly dismissedSessionStorageKey = "talk-bi:onboarding:dismissed-session";
  private static readonly userStateStorageKeyPrefix = "talk-bi:onboarding:user-state";

  public dismissForCurrentUser(keycloak: Keycloak): void {
    this.setUserState(keycloak, "dismissed");
  }

  public completeForCurrentUser(keycloak: Keycloak): void {
    this.setUserState(keycloak, "completed");
  }

  public isHandledForCurrentUser(keycloak: Keycloak): boolean {
    const storageKey = this.getUserStateStorageKey(keycloak);
    if (!storageKey) {
      return false;
    }

    const state = this.readStorage(localStorage, storageKey);
    return state === "dismissed" || state === "completed";
  }

  public dismissForCurrentSession(keycloak: Keycloak): void {
    const sessionId = this.getSessionId(keycloak);
    if (!sessionId) {
      return;
    }

    this.writeStorage(sessionStorage, OnboardingSessionService.dismissedSessionStorageKey, sessionId);
  }

  public isDismissedForCurrentSession(keycloak: Keycloak): boolean {
    const sessionId = this.getSessionId(keycloak);
    const storedSessionId = this.readStorage(sessionStorage, OnboardingSessionService.dismissedSessionStorageKey);

    if (!sessionId || !storedSessionId) {
      return false;
    }

    if (storedSessionId !== sessionId) {
      this.clearDismissal();
      return false;
    }

    return true;
  }

  public clearDismissal(): void {
    try {
      sessionStorage.removeItem(OnboardingSessionService.dismissedSessionStorageKey);
    } catch {
      // Browser privacy settings can disable storage. Onboarding must remain navigable.
    }
  }

  private getSessionId(keycloak: Keycloak): string | null {
    const parsedToken = keycloak.tokenParsed;
    if (!parsedToken || typeof parsedToken !== "object") {
      return null;
    }

    const sessionId = (parsedToken as { sid?: unknown }).sid;
    return typeof sessionId === "string" && sessionId.trim().length > 0 ? sessionId : null;
  }

  private setUserState(keycloak: Keycloak, state: "dismissed" | "completed"): void {
    const storageKey = this.getUserStateStorageKey(keycloak);
    if (!storageKey) {
      return;
    }

    this.writeStorage(localStorage, storageKey, state);
  }

  private getUserStateStorageKey(keycloak: Keycloak): string | null {
    const parsedToken = keycloak.tokenParsed;
    if (!parsedToken || typeof parsedToken !== "object") {
      return null;
    }

    const userId = (parsedToken as { sub?: unknown }).sub;
    if (typeof userId !== "string" || userId.trim().length === 0) {
      return null;
    }

    return `${OnboardingSessionService.userStateStorageKeyPrefix}:${userId}`;
  }

  private readStorage(storage: Storage, key: string): string | null {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(storage: Storage, key: string, value: string): void {
    try {
      storage.setItem(key, value);
    } catch {
      // Persistence is best-effort; never trap the user in onboarding.
    }
  }
}
